import ScButton from "@/src/components/ui-kit/button";
import WebDropZone from "@/src/components/ui-kit/web-drop-zone";
import {
  BYTES_PER_MB,
  MAX_IMAGE_SIZE_MB,
} from "@/src/constants/files-extensions";
import { useTheme } from "@/src/styles/theme-provider";
import { webFileToAsset } from "@/src/utils/attachment.utils";
import * as DocumentPicker from "expo-document-picker";
import * as ImagePicker from "expo-image-picker";
import { AddCircle } from "iconsax-react-nativejs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Image,
  Platform,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import Toast from "react-native-toast-message";

const IMAGE_MIME_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/svg+xml",
  "image/bmp",
];

type FileUploadProps = Readonly<{
  label?: string;
  onFileSelect?: (
    file:
      | DocumentPicker.DocumentPickerAsset
      | ImagePicker.ImagePickerAsset
      | (DocumentPicker.DocumentPickerAsset | ImagePicker.ImagePickerAsset)[],
  ) => void;
  onDropRejected?: () => void;
  acceptedFormats?: string;
  maxSize?: number;
  multiple?: boolean;
  previewFile?: { uri: string; fileName?: string } | null;
}>;

export default function ImageUpload({
  label,
  onFileSelect,
  onDropRejected,
  acceptedFormats,
  maxSize = MAX_IMAGE_SIZE_MB,
  multiple = false,
  previewFile = null,
}: FileUploadProps) {
  const { t } = useTranslation();
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);

  const { primaryPalette, theme } = useTheme();
  const removeButtonBg = primaryPalette.default;
  const removeButtonText = theme === "dark" ? "#000000" : "#ffffff";
  const changeButtonBg = primaryPalette["2"];

  const showTooLargeToast = () => {
    Toast.show({
      type: "error",
      text1: t("ERROR_IMAGE_TOO_LARGE", { maxSize }),
    });
  };

  const acceptSingleFile = (file: {
    uri: string;
    name?: string;
    size?: number;
  }) => {
    if (file.size && file.size > maxSize * BYTES_PER_MB) {
      showTooLargeToast();
      return;
    }
    setSelectedFile(file.uri);
    setFileName(file.name ?? null);
    onFileSelect?.(file as any);
  };

  const handleFilePick = async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: ["image/*"],
        copyToCacheDirectory: true,
        multiple,
      });

      if (!result.canceled && result.assets && result.assets.length > 0) {
        if (multiple && result.assets.length > 1) {
          const validFiles = result.assets.filter((file) => {
            if (file.size && file.size > maxSize * BYTES_PER_MB) {
              showTooLargeToast();
              return false;
            }
            return true;
          });
          if (validFiles.length > 0) onFileSelect?.(validFiles);
        } else {
          acceptSingleFile(result.assets[0]);
        }
      }
    } catch {
      Alert.alert(t("ERROR"), t("ERROR_GENERIC"));
    }
  };

  const handleDropFiles = (files: File[]) => {
    // acceptedMimeTypes on the inner WebDropZone already filters,
    // but guard here too just in case
    const imageFiles = files.filter((f) => IMAGE_MIME_TYPES.includes(f.type));
    if (imageFiles.length === 0) return;

    const file = imageFiles[0];
    if (multiple) {
      const valid = imageFiles
        .filter((f) => f.size <= maxSize * BYTES_PER_MB)
        .map(webFileToAsset);
      if (valid.length !== imageFiles.length) showTooLargeToast();
      if (valid.length > 0) onFileSelect?.(valid as any);
      return;
    }
    acceptSingleFile(webFileToAsset(file));
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFileName(null);
    onFileSelect?.(null as any);
  };

  const displayFile = previewFile?.uri || selectedFile;
  const displayFileName = previewFile?.fileName || fileName;

  // Only show label when it is a non-empty string
  const showLabel = typeof label === "string" ? label.trim().length > 0 : true;

  return (
    <View className="mb-4">
      {showLabel && (
        <Text className="pb-2 font-medium text-onyx">
          {label || t("LABEL_ATTACHMENT")}
        </Text>
      )}

      {displayFile ? (
        <View className="relative rounded-lg overflow-hidden h-48 border-2 border-ash-3">
          <Image
            source={{ uri: displayFile }}
            className="w-full h-full absolute"
            resizeMode="cover"
          />
          <View className="absolute inset-0 bg-black/40" />
          <View className="absolute inset-0 items-center justify-center">
            {displayFileName && (
              <Text
                className="text-white font-medium text-sm mb-4 px-4"
                numberOfLines={1}
              >
                {displayFileName}
              </Text>
            )}
            <View className="flex-row gap-3">
              <ScButton
                text="BTN_CHANGE"
                onPress={handleFilePick}
                mode="outlined"
                style={{ backgroundColor: changeButtonBg }}
              />
              <ScButton
                text="BTN_REMOVE"
                onPress={handleRemoveFile}
                mode="contained"
                backgroundColor={removeButtonBg}
                textColor={removeButtonText}
              />
            </View>
          </View>
        </View>
      ) : (
        <WebDropZone
          onDropFiles={handleDropFiles}
          acceptedMimeTypes={IMAGE_MIME_TYPES}
          onDropRejected={onDropRejected}
          renderChildren={(isDragActive) => (
            <TouchableOpacity
              onPress={handleFilePick}
              className={`border-2 border-dashed rounded-lg p-6 items-center justify-center bg-chalk hover:bg-ash-3 ${
                Platform.OS === "web" && isDragActive
                  ? "border-primary bg-primary-2"
                  : "border-ash-3"
              }`}
              activeOpacity={0.7}
            >
              <View className="items-center">
                <View className="w-12 h-12 bg-ash rounded-full items-center justify-center mb-2">
                  <AddCircle size={28} color={primaryPalette.default} />
                </View>
                <Text className="text-onyx font-medium text-sm mb-1">
                  <Text className="text-primary underline underline-offset-2">
                    {t("LABEL_UPLOAD_IMAGE")}
                  </Text>{" "}
                  {t("LABEL_OR")} {t("LABEL_UPLOAD_OR_DRAG_DROP").toLowerCase()}
                </Text>
                <Text className="text-ash-1 text-xs">{acceptedFormats}</Text>
              </View>
            </TouchableOpacity>
          )}
        />
      )}
    </View>
  );
}
